import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Column, createGrid, ExcelRow, GridApi, GridOptions } from 'ag-grid-community';

import { getGridElement } from '../../shared/ag-grid/ag-grid-functions';

@Injectable({
  providedIn: 'root',
})
export class AGGridExcelService {
  private gridApi: GridApi;

  private static readonly DATE_EU = 'dd.mm.yyyy;@';
  private static readonly CURRENCY = '#,##0.00 \\€';
  private static readonly CURRENCY_NEGATIVE_SIGN = '-#,##0.00 \\€';
  private static readonly CURRENCYUSD = '-#,##0.00 \\$';
  private static readonly CURRENCYUSD_NEGATIVE_SIGN = '-#,##0.00 \\$';
  private static readonly PERCENT = '0.00 \\%';

  private excelStyles;
  private ts: TranslateService;

  private setExcelStyles(): void {
    this.excelStyles = [
      {
        id: 'dateFormat',
        dataType: 'dateTime',
        numberFormat: { format: AGGridExcelService.DATE_EU },
      },
      {
        id: 'currencyFormat',
        numberFormat: { format: AGGridExcelService.CURRENCY },
      },
      {
        id: 'currencyFormatUSD',
        numberFormat: { format: AGGridExcelService.CURRENCYUSD },
      },
      {
        id: 'currencyFormatNegativeSign',
        numberFormat: { format: AGGridExcelService.CURRENCY_NEGATIVE_SIGN },
      },
      {
        id: 'currencyFormatUSDNegativeSign',
        numberFormat: { format: AGGridExcelService.CURRENCYUSD_NEGATIVE_SIGN },
      },
      {
        id: 'percentFormat',
        numberFormat: { format: AGGridExcelService.PERCENT },
      },
      {
        id: 'bigHeader',
        font: { size: 25 },
      },
      {
        id: 'alignLeftDateFormat',
        dataType: 'dateTime',
        alignment: {
          horizontal: 'Left',
          vertical: 'Bottom',
        },
        numberFormat: { format: AGGridExcelService.DATE_EU },
      },
      {
        id: 'header',
        interior: {
          color: '#CCCCCC',
          pattern: 'Solid',
        },
        borders: {
          borderBottom: {
            color: '#5687f5',
            lineStyle: 'Continuous',
            weight: 1,
          },
          borderLeft: {
            color: '#5687f5',
            lineStyle: 'Continuous',
            weight: 1,
          },
          borderRight: {
            color: '#5687f5',
            lineStyle: 'Continuous',
            weight: 1,
          },
          borderTop: {
            color: '#5687f5',
            lineStyle: 'Continuous',
            weight: 1,
          },
        },
      },
    ];
  }

  // TODO: dont (in terms of _never_) pass services to a getter ⚡️ (and dont call a setter 😎)
  public getExcelStyles(ts: TranslateService): string {
    this.setExcelStyles();
    this.ts = ts;
    return this.excelStyles;
  }

  private getColumnsWithoutButton(): string[] {
    const allColumns: Column[] = this.gridApi.getAllDisplayedColumns();
    const columnsWithoutButton: string[] = [];
    allColumns.forEach(column => {
      if (column.getColId() !== 'buttonColumn') {
        this.setHeaderName(column);
        columnsWithoutButton.push(column.getColId());
      }
    });
    return columnsWithoutButton;
  }

  private setHeaderName(column: Column): void {
    if (column.getColDef().headerTooltip) column.getColDef().headerName = column.getColDef().headerTooltip;
  }

  private getCustomizedRows(headLine: string): () => ExcelRow[] {
    const customizedRows: () => ExcelRow[] = () => [
      {
        cells: [
          {
            styleId: 'bigHeader',
            data: { value: headLine, type: 'String' },
          },
        ],
      },
      {
        cells: [
          {
            data: { value: this.ts.instant('EXCEL_EXPORT_DATE'), type: 'String' },
            mergeAcross: 1,
          },
          {
            styleId: 'alignLeftDateFormat',
            data: { value: new Date().toISOString().slice(0, 10), type: 'DateTime' },
          },
        ],
      },
      {
        cells: [
          {
            data: { value: this.ts.instant('EXCEL_EXPORT_CONFIDENTIAL_LEVEL'), type: 'String' },
            mergeAcross: 1,
          },
          {
            data: { value: this.ts.instant('EXCEL_EXPORT_CONFIDENTIAL'), type: 'String' },
          },
        ],
      },
    ];
    return customizedRows;
  }

  public getExcelFrame(fileName: string, headLine: string, gridOptions: GridOptions): any {
    this.gridApi = createGrid(getGridElement(), gridOptions);

    const customizedRows = this.getCustomizedRows(headLine);

    return {
      columnKeys: this.getColumnsWithoutButton(),

      sheetName: 'Overview',
      fileName: this.getFullFileName(fileName),
      exportMode: undefined,
      suppressTextAsCDATA: false,
      rowHeight: undefined,
      headerRowHeight: undefined,
      prependContent: customizedRows(),
    };
  }

  private getFullFileName(fileName: string): string {
    return `${this.ts.instant('EXCEL_EXPORT_CONFIDENTIAL').toUpperCase()}_${new Date()
      .toISOString()
      .slice(0, 10)}_${fileName}`;
  }
}
